import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "./AdminLayout";
import { api, formatApiError, uploadToS3 } from "@/lib/api";
import { Section, TextField, TextArea, Toggle, AdminButton } from "@/components/admin/AdminUI";
import { Pencil, Trash2, Plus, Upload, Loader2, CheckCircle2, AlertCircle, RefreshCw, X, Play } from "lucide-react";

/**
 * Reels admin — video-first, thumbnail auto-generated by the browser.
 *
 * Flow:
 *  - "New Reel" auto-creates a draft on the server so uploads auto-associate.
 *  - The MediaUploader is replaced by a video-only upload box; on completion
 *    the video preview plays inline so you see exactly what's on production.
 *  - Bulk mode: pick multiple videos → one reel per video, titled from the file
 *    name (user can rename each in the list). Great for uploading a batch of
 *    reels in one go.
 */

const DRAFT_PLACEHOLDER_TITLE = "Untitled reel";

const emptyDetails = {
  title: "", caption: "", wedding_id: "",
  featured_home: false, display_order: 0,
  video_url: "", video_media_id: null, video_storage_key: null,
};

export default function ReelsAdmin() {
  const [list, setList] = useState([]);
  const [weddings, setWeddings] = useState([]);
  const [editing, setEditing] = useState(null); // {id, ...details, isDraft, hasEdited}
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState("");
  const [videoUploading, setVideoUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  // Bulk uploads
  const [bulkQueue, setBulkQueue] = useState([]); // {id, name, status, error}
  const [bulkRunning, setBulkRunning] = useState(false);
  const creatingRef = React.useRef(false); // sync guard to block double-click races

  const load = useCallback(async () => {
    try {
      // Silently sweep any abandoned drafts before listing so the admin table stays clean
      try { await api.post("/reels/sweep-drafts"); } catch (_) {}
      const [r1, r2] = await Promise.all([
        api.get("/reels", { params: { include_drafts: true } }),
        api.get("/weddings", { params: { include_drafts: true } }),
      ]);
      setList(r1.data || []);
      setWeddings(r2.data || []);
    } catch (_) {}
  }, []);
  useEffect(() => { load(); }, [load]);

  /* --------------------------- CREATE (draft) --------------------------- */
  const startNew = async () => {
    if (creatingRef.current) return; // block re-entry
    creatingRef.current = true;
    setCreating(true); setErr("");
    try {
      const { data } = await api.post("/reels", { title: DRAFT_PLACEHOLDER_TITLE });
      setEditing({ ...emptyDetails, id: data.id, isDraft: true, hasEdited: false });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  };

  const startEdit = (r) => {
    setEditing({
      ...emptyDetails,
      id: r.id,
      title: r.title === DRAFT_PLACEHOLDER_TITLE ? "" : (r.title || ""),
      caption: r.caption || "",
      wedding_id: r.wedding_id || "",
      featured_home: !!r.featured_home,
      display_order: r.display_order || 0,
      video_url: r.video_url || "",
      video_media_id: r.video_media_id || null,
      video_storage_key: r.video_storage_key || null,
      isDraft: false, hasEdited: false,
    });
  };

  /* --------------------------- VIDEO UPLOAD ---------------------------- */
  const handleVideoFile = async (file) => {
    if (!file || !editing) return;
    setUploadError("");
    setVideoUploading(true);
    try {
      const media = await uploadToS3(file, "video", {
        folder: "reels",
        purpose: "reel_video",
        association: { reel_id: editing.id },
      });
      if (!media) return;
      // If replacing an existing video, drop the old media row
      const prevId = editing.video_media_id;
      setEditing((prev) => prev ? ({
        ...prev,
        hasEdited: true,
        video_url: media.url,
        video_media_id: media.id,
        video_storage_key: media.storage_key,
      }) : prev);
      if (prevId && prevId !== media.id) {
        try { await api.delete(`/media/${prevId}`); } catch (_) {}
      }
    } catch (e) {
      setUploadError(e.message || "Upload failed");
    } finally {
      setVideoUploading(false);
    }
  };

  const removeVideo = async () => {
    if (!editing?.video_media_id) return;
    if (!window.confirm("Remove this video? It will be deleted from S3.")) return;
    try { await api.delete(`/media/${editing.video_media_id}`); } catch (_) {}
    setEditing((prev) => prev ? ({
      ...prev, hasEdited: true, video_url: "", video_media_id: null, video_storage_key: null,
    }) : prev);
  };

  /* ------------------------------- SAVE -------------------------------- */
  const save = async (e) => {
    e?.preventDefault?.();
    if (!editing) return;
    if (!editing.title.trim()) {
      setErr("Title is required.");
      return;
    }
    setSaving(true); setErr("");
    try {
      const payload = {
        title: editing.title.trim(),
        caption: editing.caption || "",
        wedding_id: editing.wedding_id || null,
        featured_home: !!editing.featured_home,
        display_order: Number(editing.display_order) || 0,
        video_url: editing.video_url || "",
        video_storage_key: editing.video_storage_key || null,
      };
      await api.put(`/reels/${editing.id}`, payload);
      setEditing(null);
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const cancel = async () => {
    if (!editing) return;
    if (editing.isDraft && !editing.hasEdited) {
      try { await api.delete(`/reels/${editing.id}`); } catch (_) {}
    }
    setEditing(null);
    await load();
  };

  const del = async (r) => {
    if (!window.confirm(`Delete "${r.title || 'this reel'}"? The video will be removed from S3.`)) return;
    // Cascade: delete the reel's video media if present
    if (r.video_media_id) {
      try { await api.delete(`/media/${r.video_media_id}`); } catch (_) {}
    }
    try { await api.delete(`/reels/${r.id}`); } catch (_) {}
    await load();
  };

  /* --------------------------- BULK UPLOAD ----------------------------- */
  const handleBulkFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("video/"));
    if (!files.length) return;
    setBulkRunning(true);
    const queue = files.map((f, i) => ({ id: `${Date.now()}-${i}`, name: f.name, status: "queued", error: "" }));
    setBulkQueue(queue);

    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const uid = queue[i].id;
      setBulkQueue((prev) => prev.map((q) => q.id === uid ? { ...q, status: "creating" } : q));
      try {
        // 1. Create reel with title from filename
        const derivedTitle = f.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim() || DRAFT_PLACEHOLDER_TITLE;
        const { data: reel } = await api.post("/reels", { title: derivedTitle });
        // 2. Upload video associated to that reel
        setBulkQueue((prev) => prev.map((q) => q.id === uid ? { ...q, status: "uploading" } : q));
        const media = await uploadToS3(f, "video", {
          folder: "reels",
          purpose: "reel_video",
          association: { reel_id: reel.id },
        });
        if (!media) throw new Error("Upload cancelled");
        // 3. Persist the video_storage_key on the reel
        await api.put(`/reels/${reel.id}`, {
          title: derivedTitle,
          video_storage_key: media.storage_key,
          video_url: media.url,
        });
        setBulkQueue((prev) => prev.map((q) => q.id === uid ? { ...q, status: "done" } : q));
      } catch (e) {
        setBulkQueue((prev) => prev.map((q) => q.id === uid ? { ...q, status: "failed", error: e.message } : q));
      }
    }
    setBulkRunning(false);
    await load();
  };

  const clearBulkFinished = () => {
    setBulkQueue((prev) => prev.filter((q) => q.status !== "done" && q.status !== "failed"));
  };

  const bulkActive = bulkQueue.some((q) => q.status === "uploading" || q.status === "creating" || q.status === "queued");
  const bulkFailed = bulkQueue.some((q) => q.status === "failed");

  /* ------------------------------- RENDER ------------------------------ */
  return (
    <AdminLayout>
      <Section
        title="Reels"
        actions={
          <div className="flex items-center gap-3">
            <label
              data-testid="reels-bulk-upload"
              className="btn-pill btn-outline cursor-pointer !py-2 !px-4 text-[10px]"
            >
              <Upload size={14} /> Bulk upload videos
              <input
                type="file" multiple accept="video/*" className="hidden"
                onChange={(e) => { handleBulkFiles(e.target.files); e.target.value = ""; }}
                disabled={bulkRunning}
              />
            </label>
            <AdminButton testId="new-reel" onClick={startNew} disabled={creating}>
              <Plus size={14} /> {creating ? "Creating…" : "New Reel"}
            </AdminButton>
          </div>
        }
      >
        {err && !editing && <div className="text-red-400 text-sm mb-4">{err}</div>}

        {/* Bulk queue */}
        {bulkQueue.length > 0 && (
          <div className="mb-4 space-y-1 max-h-56 overflow-y-auto pr-1 border border-[color:var(--border)] p-3" data-testid="reels-bulk-log">
            <div className="eyebrow text-[color:var(--text-muted)] mb-2 flex items-center justify-between">
              <span>Bulk upload queue</span>
              {!bulkActive && (
                <button type="button" onClick={clearBulkFinished} className="eyebrow link-underline">Clear log</button>
              )}
            </div>
            {bulkQueue.map((q) => (
              <div key={q.id} className="flex items-center gap-2 text-xs px-2 py-1 bg-[color:var(--surface)]">
                {(q.status === "uploading" || q.status === "creating" || q.status === "queued") && <Loader2 size={12} className="animate-spin text-[color:var(--accent)]" />}
                {q.status === "done" && <CheckCircle2 size={12} className="text-emerald-400" />}
                {q.status === "failed" && <AlertCircle size={12} className="text-red-400" />}
                <span className="truncate flex-1">{q.name}</span>
                <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-muted)]">{q.status}</span>
                {q.status === "failed" && <span className="text-red-400 text-[10px] max-w-[220px] truncate" title={q.error}>{q.error}</span>}
              </div>
            ))}
            {bulkFailed && (
              <div className="text-[10px] text-[color:var(--text-muted)] mt-2">Failed uploads left the reel row empty — re-drop the file into the reel editor to retry.</div>
            )}
          </div>
        )}

        <div className="border border-[color:var(--border)]">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--surface)]">
              <tr className="text-left eyebrow text-[color:var(--text-muted)]">
                <th className="p-4">Preview</th>
                <th className="p-4">Title</th>
                <th className="p-4 hidden md:table-cell">Wedding</th>
                <th className="p-4">Home</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.id} data-testid={`row-reel-${r.id}`} className="border-t border-[color:var(--border)]">
                  <td className="p-4">
                    {r.video_url ? (
                      <video
                        src={r.video_url}
                        className="w-16 h-24 object-cover bg-black"
                        muted
                        playsInline
                        preload="metadata"
                        onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
                        onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
                      />
                    ) : (
                      <div className="w-16 h-24 bg-[color:var(--surface)] flex items-center justify-center text-[color:var(--text-muted)] text-[9px] tracking-wider uppercase">
                        No video
                      </div>
                    )}
                  </td>
                  <td className="p-4 font-display italic text-lg">
                    {r.title === DRAFT_PLACEHOLDER_TITLE || !(r.title || "").trim() ? (
                      <span className="text-[color:var(--text-muted)]">
                        Untitled reel
                        <span className="ml-2 inline-block px-1.5 py-0.5 border border-[color:var(--accent)] text-[color:var(--accent)] text-[9px] tracking-widest align-middle not-italic font-sans">
                          DRAFT
                        </span>
                      </span>
                    ) : (
                      r.title
                    )}
                  </td>
                  <td className="p-4 hidden md:table-cell text-[color:var(--text-muted)]">
                    {weddings.find((w) => w.id === r.wedding_id)?.name || "—"}
                  </td>
                  <td className="p-4">{r.featured_home ? "★" : ""}</td>
                  <td className="p-4 text-right whitespace-nowrap">
                    <button data-testid={`edit-reel-${r.id}`} onClick={() => startEdit(r)} className="mr-3 text-[color:var(--text-muted)] hover:text-[color:var(--accent)]"><Pencil size={16} /></button>
                    <button data-testid={`delete-reel-${r.id}`} onClick={() => del(r)} className="text-[color:var(--text-muted)] hover:text-red-400"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
              {list.length === 0 && (
                <tr>
                  <td colSpan="5" className="p-10 text-center text-[color:var(--text-muted)]">
                    No reels yet — click <span className="text-[color:var(--accent)]">New Reel</span> or bulk upload above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Edit modal */}
      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm overflow-y-auto p-4 md:p-10"
          onClick={(e) => e.target === e.currentTarget && cancel()}
        >
          <form onSubmit={save} className="max-w-3xl mx-auto bg-[color:var(--surface)] border border-[color:var(--border)] p-6 md:p-10">
            <div className="flex items-start justify-between mb-6 gap-4">
              <div>
                <div className="eyebrow text-[color:var(--text-muted)]">
                  {editing.isDraft ? "New Reel (draft)" : "Editing reel"}
                </div>
                <h3 className="font-display italic text-3xl mt-1">
                  {editing.title || "Untitled reel"}
                </h3>
              </div>
            </div>

            {/* Details */}
            <div className="mb-6 space-y-4">
              <TextField testId="reel-title" label="Title *" value={editing.title}
                         onChange={(v) => setEditing((p) => ({ ...p, title: v, hasEdited: true }))} />
              <label className="block">
                <div className="eyebrow mb-2">Associated wedding (optional)</div>
                <select
                  data-testid="reel-wedding"
                  value={editing.wedding_id || ""}
                  onChange={(e) => setEditing((p) => ({ ...p, wedding_id: e.target.value, hasEdited: true }))}
                  className="w-full bg-transparent border border-[color:var(--border)] px-3 py-2.5 text-sm"
                >
                  <option value="">— None —</option>
                  {weddings.filter((w) => w.name && w.name !== "Untitled wedding").map((w) => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              </label>
              <TextArea testId="reel-caption" label="Caption" value={editing.caption} rows={2}
                        onChange={(v) => setEditing((p) => ({ ...p, caption: v, hasEdited: true }))} />
              <div className="grid grid-cols-2 gap-6">
                <TextField testId="reel-order" label="Display order" type="number" value={editing.display_order}
                           onChange={(v) => setEditing((p) => ({ ...p, display_order: parseInt(v) || 0, hasEdited: true }))} />
                <Toggle testId="reel-featured" label="Featured on homepage" value={editing.featured_home}
                        onChange={(v) => setEditing((p) => ({ ...p, featured_home: v, hasEdited: true }))} />
              </div>
            </div>

            {/* Video */}
            <div className="mb-6">
              <div className="eyebrow mb-3">Video</div>
              {editing.video_url ? (
                <div className="space-y-3">
                  <div className="relative aspect-[9/16] max-w-[280px] bg-black">
                    <video
                      src={editing.video_url}
                      controls muted playsInline
                      preload="metadata"
                      className="w-full h-full object-contain"
                      data-testid="reel-video-preview"
                    />
                  </div>
                  <div className="flex gap-3 flex-wrap">
                    <label className="btn-pill btn-outline cursor-pointer !py-2 !px-4 text-[10px]">
                      <RefreshCw size={12} /> Replace video
                      <input
                        type="file" accept="video/*" className="hidden"
                        onChange={(e) => { handleVideoFile(e.target.files?.[0]); e.target.value = ""; }}
                        disabled={videoUploading}
                      />
                    </label>
                    <button type="button" onClick={removeVideo}
                      className="btn-pill btn-outline !py-2 !px-4 text-[10px] !border-red-400/40 !text-red-400 hover:!bg-red-400/10">
                      <Trash2 size={12} /> Remove
                    </button>
                  </div>
                </div>
              ) : videoUploading ? (
                <div className="border border-dashed border-[color:var(--accent)] p-10 text-center">
                  <Loader2 size={24} className="animate-spin mx-auto mb-2 text-[color:var(--accent)]" />
                  <div className="text-sm text-[color:var(--text-muted)]">Uploading… don&apos;t close this window.</div>
                </div>
              ) : (
                <label
                  data-testid="reel-video-upload"
                  className="cursor-pointer block border border-dashed border-[color:var(--border)] hover:border-[color:var(--accent)] p-10 text-center"
                >
                  <Upload size={20} className="mx-auto mb-2 text-[color:var(--text-muted)]" />
                  <div className="text-sm">Click or drop a video file to upload</div>
                  <div className="text-xs text-[color:var(--text-muted)] mt-1">Portrait 9:16 recommended · The first frame becomes the poster automatically.</div>
                  <input
                    type="file" accept="video/*" className="hidden"
                    onChange={(e) => { handleVideoFile(e.target.files?.[0]); e.target.value = ""; }}
                    disabled={videoUploading}
                  />
                </label>
              )}
              {uploadError && (
                <div className="text-red-400 text-xs mt-2" data-testid="reel-upload-error">{uploadError}</div>
              )}
            </div>

            {err && <div className="text-red-400 text-sm mb-4" data-testid="reel-form-error">{err}</div>}
            <div className="flex justify-between items-center gap-3 pt-4 border-t border-[color:var(--border)]">
              <div className="text-xs text-[color:var(--text-muted)]">
                Video uploads save immediately. <span className="text-[color:var(--text)]">Save</span> persists title, caption and settings.
              </div>
              <div className="flex gap-3">
                <AdminButton variant="ghost" type="button" onClick={cancel} testId="reel-cancel">
                  {editing.isDraft && !editing.hasEdited ? "Discard draft" : "Close"}
                </AdminButton>
                <AdminButton type="submit" testId="reel-save" disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </AdminButton>
              </div>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}
